'use client';

import { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/nextjs';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to Sentry
    Sentry.captureException(error, { extra: { errorInfo } });
    
    this.setState({
      error,
      errorInfo,
    });
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      
      return (
        <div className="p-6 max-w-2xl mx-auto">
          <h2 className="text-2xl font-bold mb-4">Something went wrong</h2>
          <details className="mb-4">
            <summary className="cursor-pointer text-orange-700 mb-2">Error details</summary>
            <pre className="bg-gray-100 p-4 rounded overflow-auto text-sm">
              {this.state.error?.toString()}
              <br />
              {this.state.errorInfo?.componentStack}
            </pre>
          </details>
          <button
            onClick={() => {
              this.setState({ hasError: false, error: null, errorInfo: null });
              window.location.reload();
            }}
            className="px-4 py-2 bg-orange-700 text-white rounded hover:bg-orange-900"
          >
            Try again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export function withErrorBoundary<T extends object>(
  Component: React.ComponentType<T>,
  FallbackComponent?: React.ComponentType<{ error: Error; reset: () => void }>
) {
  return function WithErrorBoundary(props: T) {
    return (
      <ErrorBoundary
        fallback={
          FallbackComponent ? (
            <ErrorBoundaryFallback FallbackComponent={FallbackComponent} />
          ) : null
        }
      >
        <Component {...props} />
      </ErrorBoundary>
    );
  };
}

function ErrorBoundaryFallback({
  FallbackComponent,
  error,
  resetErrorBoundary,
}: {
  FallbackComponent: React.ComponentType<{ error: Error; reset: () => void }>;
  error?: Error;
  resetErrorBoundary?: () => void;
}) {
  return (
    <FallbackComponent
      error={error || new Error('Unknown error')}
      reset={() => {
        if (resetErrorBoundary) {
          resetErrorBoundary();
        } else {
          window.location.reload();
        }
      }}
    />
  );
}
